/**
 * FluidOrb — a WebGL orb with drifting fluid shading that moves as you speak.
 *
 * Ported from `swamimalode07/rare-ui/fluid-orb` (shadcn registry) with one
 * addition the Probe page needs: an `activityRef` in [0, 1] that the render
 * loop reads every frame. Activity speeds the drift, deepens the turbulence
 * and swells the orb — so the shape breathes with the microphone while you
 * talk and with the reply audio while the agent does — without a React
 * re-render per frame. The ref is deliberately a ref: audio levels arrive at
 * 60 Hz and would otherwise re-render the whole page at that rate.
 *
 * Time is accumulated on the CPU as a phase (`phase += dt * speed(activity)`)
 * rather than multiplying wall time by a speed: multiplying makes the noise
 * field jump every time the level changes; integrating keeps it continuous.
 *
 * `color` accepts any CSS color, including the theme's `oklch(...)` tokens —
 * it is resolved through a 2D canvas so the shader gets RGB. Left unset it
 * is the theme's `--primary`, and it follows theme changes live.
 *
 * @module components/ui/fluid-orb
 */
import React, { useEffect, useRef } from "react";

import { cn } from "~/lib/utils";

export type FluidOrbProps = React.ComponentProps<"div"> & {
  size?: number;
  /** Any CSS color, `var()` included; defaults to the theme's `--primary`. */
  color?: string;
  /** 0 = at rest, 1 = shouting. Read every frame; never triggers a render. */
  activityRef?: React.RefObject<number>;
};

const VERT = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_activity;
uniform vec3 u_color;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.6;
  for (int i = 0; i < 3; i++) {
    v += a * noise(p);
    p *= 2.0;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  float t = u_time * 0.22;

  vec2 drift = vec2(
    sin(t) + 0.6 * sin(t * 1.7 + 1.3),
    cos(t * 0.8) + 0.6 * cos(t * 1.3 + 2.1)
  );

  // Louder ⇒ the fluid is pushed further and folded harder.
  float push = 0.7 + 0.5 * u_activity;
  vec2 p = vec2(uv.x * 1.8, uv.y * 1.0) + drift * push;

  vec2 q = vec2(fbm(p + drift), fbm(p + vec2(3.2, 1.5) - drift));
  float f = fbm(p + (1.2 + 1.6 * u_activity) * q);

  float g = clamp(1.0 - uv.y, 0.0, 1.0);
  float anchor = smoothstep(0.0, 0.3, uv.y);
  float shade = clamp(g + (f - 0.5) * (0.8 + 0.5 * u_activity) * anchor, 0.0, 1.0);

  vec3 white = vec3(0.99, 1.0, 1.0);
  vec3 light = mix(white, u_color, 0.5);
  // A theme whose primary is itself near white (monochrome dark themes)
  // would leave white on white, and the fluid would vanish into a flat disc:
  // deepen the dark layer as the colour approaches white, so it still moves.
  float lum = dot(u_color, vec3(0.2126, 0.7152, 0.0722));
  vec3 dark = u_color * mix(1.0, 0.45, smoothstep(0.55, 0.95, lum));

  vec3 col = white;
  col = mix(col, light, smoothstep(0.28, 0.52, shade));
  col = mix(col, dark, smoothstep(0.58, 0.88, shade));

  float edge = smoothstep(0.5, 0.49, distance(uv, vec2(0.5)));

  gl_FragColor = vec4(col * edge, edge);
}
`;

const FALLBACK_RGB: [number, number, number] = [0.1, 0.45, 0.95];
/** A colour no theme uses; if the canvas still holds it, the input did not parse. */
const SENTINEL = "#010203";

/**
 * Resolve any CSS color — hex, rgb, the theme's `oklch(...)` tokens, or a
 * `var(--primary)` chain — to 0..1 RGB. The element pass lets the cascade
 * expand `var()`s; the 2D-canvas pass converts whatever color space comes
 * back. Falls back to the registry's blue rather than to black when either
 * pass cannot make sense of the input.
 */
function cssColorToRgb(color: string): [number, number, number] {
  if (typeof document === "undefined") return FALLBACK_RGB;
  const swatch = document.createElement("span");
  swatch.style.color = color;
  if (swatch.style.color === "") return FALLBACK_RGB;
  swatch.style.position = "absolute";
  swatch.style.visibility = "hidden";
  document.body.appendChild(swatch);
  const resolved = getComputedStyle(swatch).color;
  swatch.remove();

  const probe = document.createElement("canvas");
  probe.width = 1;
  probe.height = 1;
  const ctx = probe.getContext("2d", { willReadFrequently: true });
  if (!ctx) return FALLBACK_RGB;
  ctx.fillStyle = SENTINEL;
  ctx.fillStyle = resolved;
  if (ctx.fillStyle === SENTINEL) return FALLBACK_RGB;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  if (r === undefined || g === undefined || b === undefined) return FALLBACK_RGB;
  return [r / 255, g / 255, b / 255];
}

function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

const FluidOrb = ({ size = 240, color, activityRef, className, style, ...props }: FluidOrbProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activity = activityRef;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { antialias: true, alpha: true });
    if (!gl) return;

    const program = gl.createProgram();
    const vert = compile(gl, gl.VERTEX_SHADER, VERT);
    const frag = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!program || !vert || !frag) return;

    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return;
    }
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const aPos = gl.getAttribLocation(program, "a_pos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uResolution = gl.getUniformLocation(program, "u_resolution");
    const uTime = gl.getUniformLocation(program, "u_time");
    const uActivity = gl.getUniformLocation(program, "u_activity");
    const uColor = gl.getUniformLocation(program, "u_color");
    const paint = () => gl.uniform3f(uColor, ...cssColorToRgb(color ?? "var(--primary)"));
    paint();

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const px = Math.round(size * dpr);
    canvas.width = px;
    canvas.height = px;
    gl.viewport(0, 0, px, px);
    gl.uniform2f(uResolution, px, px);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let last = performance.now();
    let phase = 0;
    // Smoothed activity: quick to rise (speech onsets are sharp), slow to
    // fall (so the orb settles instead of flickering between syllables).
    let level = 0;

    const render = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      const target = Math.max(0, Math.min(1, activity?.current ?? 0));
      const ease = target > level ? 0.35 : 0.08;
      level += (target - level) * ease;
      phase += dt * (1 + 3.5 * level);

      gl.uniform1f(uTime, reduce ? 0 : phase);
      gl.uniform1f(uActivity, level);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      canvas.style.transform = `scale(${(1 + 0.1 * level).toFixed(4)})`;
      if (!reduce) raf = requestAnimationFrame(render);
    };
    render(last);

    // A theme is a set of custom properties on <html> (plus its dark class
    // and theme id), so a `var(--primary)` resolved once goes stale the
    // moment someone switches theme. Re-resolve whenever the root's styling
    // changes; the next frame — or, without motion, one redraw — shows it.
    const theme = new MutationObserver(() => {
      paint();
      if (reduce) gl.drawArrays(gl.TRIANGLES, 0, 6);
    });
    theme.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "class", "data-theme-id"],
    });

    return () => {
      theme.disconnect();
      cancelAnimationFrame(raf);
      gl.deleteProgram(program);
      gl.deleteShader(vert);
      gl.deleteShader(frag);
      gl.deleteBuffer(buffer);
    };
  }, [size, color, activity]);

  return (
    <div
      data-slot="fluid-orb"
      className={cn("relative rounded-full", className)}
      style={{
        width: size,
        height: size,
        ...style,
      }}
      {...props}
    >
      <canvas
        ref={canvasRef}
        className="h-full w-full rounded-full transition-transform duration-75 ease-out will-change-transform"
      />
    </div>
  );
};

export default FluidOrb;
